import { Button, HStack, Icon, Text, useDisclosure } from '@chakra-ui/react';
import { DocumentTextIcon } from '@heroicons/react/outline';
import { EyeIcon } from '@heroicons/react/solid';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { SubscriptionStepStatus } from '@blockpulse3/data/shared';
import {
  FundraisingDocumentType,
  MergedDocumentSignatureStep,
  SubscriptionStepType,
  useGetSubscriptionDocumentQuery,
  useGetSubscriptionQuery,
  usePreviewSubscriptionDocumentLazyQuery,
  useValidateSubscriptionDocumentMutation,
} from '@blockpulse3/graphql/hooks';
import { b64toBlob } from '@blockpulse3/helpers';
import {
  CONTENT_LOADING,
  PreviewDocumentModal,
  SignDocumentModal,
  useErrorToast,
  useSuccessToast,
} from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { SubscriptionStep } from '../../SubscriptionStep';

type Props = {
  subscriptionStep: MergedDocumentSignatureStep;
  isDisabled: boolean;
};

export function DocumentSignatureStep({ subscriptionStep, isDisabled }: Props): JSX.Element {
  const i18nSubscription = useTranslations('Subscription');
  const [subscriptionSrc, setSubscriptionSrc] = useState<string | symbol>(CONTENT_LOADING);
  const [isPollingStarted, setIsPollingStarted] = useState<boolean>(false);
  const [isPollingLoading, setIsPollingLoading] = useState<boolean>(false);
  const [isSigningDisabled, setIsSigningDisabled] = useState<boolean>(false);

  const previewModal = useDisclosure();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const { subscriptionId = '' } = useParams();

  const { user } = useUser();

  const { data, startPolling, stopPolling } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });
  const subscriptionDocumentReq = useGetSubscriptionDocumentQuery({
    variables: { subscriptionId, documentType: FundraisingDocumentType.SUBSCRIPTION },
  });

  const [loadPreview] = usePreviewSubscriptionDocumentLazyQuery();
  const [validateSubscriptionDocument, { loading: isDocumentValidating }] =
    useValidateSubscriptionDocumentMutation();

  const subscription = data?.subscription;
  const stepStatus = subscriptionStep.status;
  const subscriptionDocument = subscriptionDocumentReq.data?.getSubscriptionDocument;
  const subscriptionDocumentStatus = subscriptionDocument?.document.status || null;

  // Display preview button only when subscription details step is in progress
  const subscriptionSteps = subscription?.data.mergedSteps;
  const subscriptionDetailsStepStatus = subscriptionSteps?.find(
    (s) => s.type === SubscriptionStepType.DETAILS,
  )?.status;
  const canPreviewDocument =
    isDisabled && subscriptionDetailsStepStatus === SubscriptionStepStatus.IN_PROGRESS;

  /* ** Is current user a signer and able to be redirected to SignatureIt ** */
  const isUserSigner =
    subscription?.identity?.individualIdentity?.id === user?.individualIdentity?.id ||
    subscription?.identity?.companyIdentity?.signer?.individualIdentity?.id ===
      user?.individualIdentity?.id;

  useEffect(() => {
    if (!isPollingStarted && stepStatus === SubscriptionStepStatus.IN_PROGRESS) {
      setIsPollingStarted(true);
      setIsPollingLoading(true);
      startPolling(2000);

      /* ** Stop polling after 10s ** */
      setTimeout(() => {
        stopPolling();
        setIsPollingLoading(false);
      }, 20_000);
    }

    /* ** Stop polling when step status is VALID ** */
    if (isPollingStarted && stepStatus === SubscriptionStepStatus.VALID) {
      stopPolling();
      setIsPollingStarted(false);
      setIsPollingLoading(false);
      subscriptionDocumentReq.refetch();
    }
  }, [isPollingStarted, startPolling, stopPolling, stepStatus, subscriptionDocumentReq]);

  const handleSubscriptionDocumentModalOpen = (onOpenModal: () => void): void => {
    // Refetch document if step is not started to handle changes with subscription amount step
    const isStepStarted = stepStatus !== SubscriptionStepStatus.NONE;
    if (!isStepStarted) {
      setSubscriptionSrc(CONTENT_LOADING);
    }

    if (subscription) {
      loadPreview({
        variables: {
          subscriptionId: subscription.id,
          documentType: subscriptionStep.documentType,
        },
        fetchPolicy: isStepStarted ? 'cache-first' : 'no-cache',
        onCompleted({ pdfURL }: { pdfURL: string }) {
          let objURL: string;
          if (pdfURL.startsWith('http')) {
            objURL = pdfURL;
          } else {
            const blob = b64toBlob(pdfURL, 'application/pdf');
            objURL = URL.createObjectURL(blob);
          }
          setSubscriptionSrc(objURL);
        },
      });
      if (subscriptionDocument) {
        const { lastRemindEmailSentAt, createdAt } = subscriptionDocument.document.signature || {};
        const shouldRemind = dayjs(lastRemindEmailSentAt || createdAt).isBefore(
          dayjs().subtract(1, 'h'),
        );
        setIsSigningDisabled(!shouldRemind);
      } else {
        setIsSigningDisabled(false);
      }
      onOpenModal();
    }
  };

  const handleSubscriptionDocumentSign = (): void => {
    validateSubscriptionDocument({
      variables: {
        validateSubscriptionDocumentInput: {
          fundraisingId: subscription?.fundraising.id || '',
          subscriptionId: subscription?.id || '',
          documentType: subscriptionStep.documentType,
        },
      },
      onCompleted(data) {
        if (data.validateSubscriptionDocument?.signingUrl) {
          window.open(data.validateSubscriptionDocument.signingUrl, '_self');
        } else {
          successToast({ title: i18nSubscription('Toast.SignatureSentSuccess') });
          setIsSigningDisabled(true);
        }
        subscriptionDocumentReq.refetch();
      },
      onError: () => {
        errorToast({ title: i18nSubscription('Toast.SignatureSentError') });
      },
    });
  };

  const handleSubscriptionDocumentCancel = (): void => {
    onClose();
  };

  return (
    <>
      <SubscriptionStep
        icon={DocumentTextIcon}
        isBadgeLoading={isPollingLoading}
        isDisabled={isDisabled}
        status={stepStatus}
        title={i18nSubscription('SubscribeDocumentSignature')}
        type={subscriptionStep.type}
        onClick={(): void => handleSubscriptionDocumentModalOpen(onOpen)}
      >
        {canPreviewDocument && (
          <Button
            color="gray.900"
            fontWeight="400"
            sx={{ textDecoration: 'underline' }}
            textAlign="left"
            variant="link"
            whiteSpace="normal"
            onClick={(): void => handleSubscriptionDocumentModalOpen(() => previewModal.onOpen())}
          >
            <HStack spacing="2">
              <Icon as={EyeIcon} boxSize="16px" color="gray.500" />
              <Text fontSize="sm">{i18nSubscription('PreviewSubscriptionDocument')}</Text>
            </HStack>
          </Button>
        )}
      </SubscriptionStep>
      <SignDocumentModal
        isDisabled={isSigningDisabled}
        isOpen={isOpen}
        isSignatureLoading={isDocumentValidating}
        isUserSigner={isUserSigner}
        src={subscriptionSrc}
        status={subscriptionDocumentStatus}
        title={i18nSubscription('SignDocumentModalTitle')}
        onCancel={handleSubscriptionDocumentCancel}
        onClose={handleSubscriptionDocumentCancel}
        onSign={handleSubscriptionDocumentSign}
      />
      <PreviewDocumentModal
        isOpen={previewModal.isOpen}
        src={subscriptionSrc}
        title={i18nSubscription('SignDocumentModalTitle')}
        onClose={previewModal.onClose}
      />
    </>
  );
}
